import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api, { API_BASE } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import CameraCapture from '../../components/CameraCapture';
import { GasPump, Camera, Plus, Check } from '@phosphor-icons/react';

export default function PilotFuel() {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ km_reading: '', fuel_amount: '', fuel_cost: '', fuel_supplier_id: '' });
  const [photos, setPhotos] = useState({ km: null, pump: null, bill: null });
  const [showCamera, setShowCamera] = useState(null);

  useEffect(() => { fetchFuel(); fetchSuppliers(); }, []);
  const fetchFuel = async () => { try { const { data } = await api.get('/pilot/fuel'); setRecords(data); } catch {} };
  const fetchSuppliers = async () => {
    try {
      const { data } = await api.get('/pilot/fuel-suppliers');
      setSuppliers((data || []).filter(s => s.status !== 'inactive'));
    } catch {}
  };

  const handlePhotoCapture = async (captureData, type) => {
    try {
      const { data } = await api.post('/upload/photo-base64', {
        photo: captureData.photo,
        gps: captureData.gps,
        label: `fuel_${type}`
      });
      setPhotos(prev => ({ ...prev, [type]: data.url }));
    } catch {}
    setShowCamera(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fuel_supplier_id) {
      alert('Please select a fuel supplier');
      return;
    }
    try {
      await api.post('/pilot/fuel', {
        km_reading: parseInt(form.km_reading) || 0,
        fuel_amount: parseFloat(form.fuel_amount) || 0,
        fuel_cost: parseFloat(form.fuel_cost) || 0,
        fuel_supplier_id: form.fuel_supplier_id,
        km_photo: photos.km || '',
        pump_photo: photos.pump || '',
        bill_photo: photos.bill || '',
      });
      fetchFuel();
      setDialogOpen(false);
      setForm({ km_reading: '', fuel_amount: '', fuel_cost: '', fuel_supplier_id: '' });
      setPhotos({ km: null, pump: null, bill: null });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const photoButtons = [
    { key: 'km', label: t('km_meter') },
    { key: 'pump', label: t('pump_reading') },
    { key: 'bill', label: t('bill') },
  ];

  return (
    <div data-testid="pilot-fuel" className="space-y-6">
      {/* Camera Overlay */}
      {showCamera && (
        <CameraCapture
          label={showCamera.label}
          onCapture={(data) => handlePhotoCapture(data, showCamera.type)}
          onClose={() => {
            setShowCamera(null);
            setDialogOpen(true);
          }}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">{t('fuel_records')}</h2>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full" data-testid="add-fuel-btn">
          <Plus size={16} className="mr-1" /> {t('add_fuel')}
        </Button>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => { if (!open && showCamera) return; setDialogOpen(open); }}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => { if (showCamera) e.preventDefault(); }}
        >
          <DialogHeader><DialogTitle>{t('add_fuel')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">{t('km_reading')} *</Label>
              <Input data-testid="fuel-km-input" type="number" value={form.km_reading} onChange={e => setForm({...form, km_reading: e.target.value})} required className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Fuel Liters *</Label>
              <Input data-testid="fuel-liters-input" type="number" step="0.01" value={form.fuel_amount} onChange={e => setForm({...form, fuel_amount: e.target.value})} required className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Bill Amount (₹) *</Label>
              <Input data-testid="fuel-bill-input" type="number" step="0.01" value={form.fuel_cost} onChange={e => setForm({...form, fuel_cost: e.target.value})} required className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Fuel Supplier *</Label>
              <select
                data-testid="fuel-supplier-select"
                value={form.fuel_supplier_id}
                onChange={e => setForm({...form, fuel_supplier_id: e.target.value})}
                required
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none rounded-full bg-white"
              >
                <option value="">Select supplier...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {suppliers.length === 0 && (
                <p className="text-[11px] text-[#FF3333] mt-1">No fuel suppliers configured. Ask admin to add one in Fuel Suppliers master.</p>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest font-medium text-[#666]">{t('take_photo')} (Live Camera)</p>
              <div className="grid grid-cols-3 gap-2">
                {photoButtons.map(pb => (
                  <button
                    key={pb.key}
                    type="button"
                    onClick={() => { setDialogOpen(false); setShowCamera({ type: pb.key, label: pb.label }); }}
                    className={`border p-3 flex flex-col items-center gap-2 min-h-[120px] transition-colors ${photos[pb.key] ? 'border-[#00C853] bg-[#00C853]/5' : 'border-dashed border-[#E5E5E5] hover:border-[#002FA7]'}`}
                    data-testid={`fuel-photo-${pb.key}`}
                  >
                    {photos[pb.key] ? (
                      <img
                        src={photos[pb.key].startsWith('/') ? `${API_BASE}${photos[pb.key]}` : photos[pb.key]}
                        alt={pb.label}
                        className="w-full h-16 object-cover"
                        data-testid={`fuel-thumb-${pb.key}`}
                      />
                    ) : (
                      <Camera size={20} className="text-[#666]" />
                    )}
                    <span className="text-xs text-[#666] flex items-center gap-1">
                      {photos[pb.key] && <Check size={12} className="text-[#00C853]" />}
                      {pb.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="fuel-save-btn" type="submit" className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full h-12">{t('save_fuel')}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        {records.map(r => (
          <div key={r.id} className="bg-white border border-[#E5E5E5] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GasPump size={20} className="text-[#002FA7]" />
                <div>
                  <p className="text-sm font-medium">{r.vehicle_number}</p>
                  <p className="text-xs text-[#666]">{r.date}</p>
                </div>
              </div>
              <span className={`text-xs uppercase tracking-widest px-2 py-1 ${r.approval_status === 'approved' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FFD700]/10 text-[#B8860B]'}`}>{r.approval_status}</span>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div><p className="text-xs text-[#666]">KM</p><p className="font-medium">{r.km_reading}</p></div>
              <div><p className="text-xs text-[#666]">{t('fuel_amount')}</p><p className="font-medium">{r.fuel_amount} L</p></div>
              <div><p className="text-xs text-[#666]">{t('fuel_cost')}</p><p className="font-medium">{r.fuel_cost}</p></div>
            </div>
            {/* Photo thumbnails */}
            {(r.km_photo || r.pump_photo || r.bill_photo) && (
              <div className="flex gap-2 mt-3">
                {r.km_photo && <img src={r.km_photo} alt="KM" className="w-16 h-16 object-cover border border-[#E5E5E5]" />}
                {r.pump_photo && <img src={r.pump_photo} alt="Pump" className="w-16 h-16 object-cover border border-[#E5E5E5]" />}
                {r.bill_photo && <img src={r.bill_photo} alt="Bill" className="w-16 h-16 object-cover border border-[#E5E5E5]" />}
              </div>
            )}
          </div>
        ))}
        {records.length === 0 && (
          <div className="text-center py-12 text-[#666]">
            <GasPump size={48} className="mx-auto mb-4 text-[#E5E5E5]" />
            <p className="text-sm">{t('no_data')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
